/**
 * Database Factory - UNIFICADO para Web y Mobile
 *
 * Factory único que soporta dos arquitecturas:
 * 1. ARQUITECTURA VIEJA: Registra repositorios específicos por plataforma
 * 2. ARQUITECTURA NUEVA: Crea repositorios unificados desde un IDatabaseAdapter
 *
 * Durante la migración gradual, ambas arquitecturas coexisten.
 *
 * USO:
 *
 * // Web (en index.ts o App.tsx):
 * import { registerDexieAdapters } from './services/storage/adapters/dexie';
 * registerDexieAdapters();
 *
 * // Mobile (en App.js):
 * import { registerSQLiteAdapters } from './services/storage/adapters/sqlite';
 * registerSQLiteAdapters();
 *
 * // Después en cualquier lugar:
 * import { getRepositories } from './services/storage';
 * const { animal, ubicacion } = getRepositories();
 */

import {
  IAnimalRepository,
  IAnimalHistoricoRepository,
  IUbicacionRepository,
  IUnidadRepository,
  IEmpresaRepository,
  IOperacionCompraVentaRepository,
  IProfileRepository,
  IDatabase,
  IGastoRepository,
  IIngresoRepository,
  IStockRepository,
  IStockHistoricoRepository,
  ICategoriaCacheRepository,
  INotaRepository,
  IEventoRepository,
  IRecordatorioRepository,
  ILluviaRepository,
  ISigsaDeclaracionRepository,
  ISyncAccionesRepository,
  IElementoMapaRepository,
  IPuntoControlRepository,
  IPuntoControlEventoRepository,
  ISiembraLoteRepository,
  INotaAnclaRepository,
  IPersonaCampoRepository,
  IApodoRepository,
  IConceptoRepository,
  ICategoriaRepository
} from './interfaces';

/**
 * Tipo de plataforma
 */
export type Platform = 'web' | 'mobile';

/**
 * Adapters que deben registrarse
 */
export interface DatabaseAdapters {
  database: IDatabase;
  // Repositorios principales
  animalRepository: IAnimalRepository;
  animalHistoricoRepository: IAnimalHistoricoRepository;
  ubicacionRepository: IUbicacionRepository;
  unidadRepository: IUnidadRepository;
  empresaRepository: IEmpresaRepository;
  operacionRepository: IOperacionCompraVentaRepository;
  profileRepository: IProfileRepository;
  // Repositorios de administración
  gastoRepository: IGastoRepository;
  ingresoRepository: IIngresoRepository;
  stockRepository: IStockRepository;
  stockHistoricoRepository: IStockHistoricoRepository;
  categoriaCacheRepository: ICategoriaCacheRepository;
  // Taxonomía de conceptos (spec 2026-08-07)
  conceptoRepository: IConceptoRepository;
  categoriaRepository: ICategoriaRepository;
  // Repositorios de notas y herramientas
  notaRepository: INotaRepository;
  eventoRepository: IEventoRepository;
  recordatorioRepository: IRecordatorioRepository;
  lluviaRepository: ILluviaRepository;
  sigsaDeclaracionRepository: ISigsaDeclaracionRepository;
  /** Cola de acciones con ejecución server-side (sync_acciones). */
  syncAccionesRepository: ISyncAccionesRepository;
  elementoMapaRepository: IElementoMapaRepository;
  puntoControlRepository: IPuntoControlRepository;
  puntoControlEventoRepository: IPuntoControlEventoRepository;
  /** Siembras de cada lote de siembra (siembras_lote). Sólo web: mobile no lo registra
   *  (no sincroniza esa tabla en esta fase) y el pull lo saltea, igual que `apodoRepository`. */
  siembraLoteRepository?: ISiembraLoteRepository | null;
  /** Notas ancladas (spec 2026-10-16). */
  notaAnclaRepository: INotaAnclaRepository;
  personaCampoRepository: IPersonaCampoRepository;
  /** Apodos de sólo lectura (web). Mobile no lo registra: el pull lo saltea. */
  apodoRepository?: IApodoRepository | null;
}

/**
 * Factory único para Web y Mobile
 *
 * Los adapters deben registrarse al inicio de la app:
 * - Web: registerDexieAdapters()
 * - Mobile: registerSQLiteAdapters()
 */
export class DatabaseFactory {
  private static instance: DatabaseFactory | null = null;
  private static adapters: DatabaseAdapters | null = null;
  private static _platform: Platform | null = null;

  private constructor() {}

  // ==================== Registro de Adapters ====================

  /**
   * Registra los adapters de base de datos
   * Debe llamarse UNA vez al inicio de la app
   */
  static registerAdapters(adapters: DatabaseAdapters, platform: Platform): void {
    if (DatabaseFactory.adapters) {
      console.warn('[DatabaseFactory] Adapters ya registrados, reemplazando...');
    }
    DatabaseFactory.adapters = adapters;
    DatabaseFactory._platform = platform;
    console.log(`[DatabaseFactory] Adapters registered for platform: ${platform}`);
  }

  /**
   * Crea repositorios unificados desde un IDatabaseAdapter
   *
   * Este método se usa en la nueva arquitectura unificada donde
   * un solo adapter por plataforma (DexieAdapter o SQLiteAdapter) se
   * comparte entre todos los repositorios.
   *
   * @param dbAdapter - El adapter de BD (DexieAdapter o SQLiteAdapter)
   * @param profileRepo - Opcional: repositorio de perfiles para inyección de dependencias
   * @returns Los repositorios creados
   */
  static createRepositoriesFromAdapter(
    dbAdapter: any,
    profileRepo?: IProfileRepository
  ): DatabaseAdapters {
    // Importar repositorios unificados dinámicamente para evitar dependencias circulares
    const { GastoRepository, ConceptoRepository, CategoriaRepository } = require('./repositories');

    return {
      database: dbAdapter,

      // Repositorio unificado de Gastos
      gastoRepository: new GastoRepository(dbAdapter, profileRepo),

      // Taxonomía de conceptos (spec 2026-08-07)
      conceptoRepository: new ConceptoRepository(dbAdapter, profileRepo),
      categoriaRepository: new CategoriaRepository(dbAdapter, profileRepo),

      // Los demás repositorios aún usan la arquitectura vieja
      // Se migrarán gradualmente en fases posteriores
      animalRepository: null as any,
      animalHistoricoRepository: null as any,
      ubicacionRepository: null as any,
      unidadRepository: null as any,
      empresaRepository: null as any,
      operacionRepository: null as any,
      profileRepository: null as any,
      ingresoRepository: null as any,
      stockRepository: null as any,
      stockHistoricoRepository: null as any,
      categoriaCacheRepository: null as any,
      notaRepository: null as any,
      eventoRepository: null as any,
      recordatorioRepository: null as any,
      lluviaRepository: null as any,
      sigsaDeclaracionRepository: null as any,
      syncAccionesRepository: null as any,
      elementoMapaRepository: null as any,
      puntoControlRepository: null as any,
      puntoControlEventoRepository: null as any,
      siembraLoteRepository: null,
      notaAnclaRepository: null as any,
      personaCampoRepository: null as any,
      apodoRepository: null
    };
  }

  /**
   * Verifica si los adapters están registrados
   */
  static isRegistered(): boolean {
    return DatabaseFactory.adapters !== null;
  }

  /**
   * Obtiene la plataforma actual
   */
  static getPlatform(): Platform | null {
    return DatabaseFactory._platform;
  }

  // ==================== Instancia ====================

  /**
   * Obtiene la instancia del factory
   */
  static getInstance(): DatabaseFactory {
    if (!DatabaseFactory.instance) {
      DatabaseFactory.instance = new DatabaseFactory();
    }
    return DatabaseFactory.instance;
  }

  /**
   * Resetea el factory (útil para testing)
   */
  static reset(): void {
    DatabaseFactory.instance = null;
    DatabaseFactory.adapters = null;
    DatabaseFactory._platform = null;
  }

  // ==================== Getters de Repositorios ====================

  private static getAdapters(): DatabaseAdapters {
    if (!DatabaseFactory.adapters) {
      throw new Error(
        '[DatabaseFactory] Adapters no registrados. ' +
        'Llama a registerDexieAdapters() (web) o registerSQLiteAdapters() (mobile) al inicio de la app.'
      );
    }
    return DatabaseFactory.adapters;
  }

  /**
   * Repositorio de animales
   */
  get animalRepository(): IAnimalRepository {
    return DatabaseFactory.getAdapters().animalRepository;
  }

  /**
   * Repositorio de historial de animales
   */
  get animalHistoricoRepository(): IAnimalHistoricoRepository {
    return DatabaseFactory.getAdapters().animalHistoricoRepository;
  }

  /**
   * Repositorio de ubicaciones
   */
  get ubicacionRepository(): IUbicacionRepository {
    return DatabaseFactory.getAdapters().ubicacionRepository;
  }

  /**
   * Repositorio de unidades
   */
  get unidadRepository(): IUnidadRepository {
    return DatabaseFactory.getAdapters().unidadRepository;
  }

  /**
   * Repositorio de empresas
   */
  get empresaRepository(): IEmpresaRepository {
    return DatabaseFactory.getAdapters().empresaRepository;
  }

  /**
   * Repositorio de operaciones
   */
  get operacionRepository(): IOperacionCompraVentaRepository {
    return DatabaseFactory.getAdapters().operacionRepository;
  }

  /**
   * Repositorio de perfiles
   */
  get profileRepository(): IProfileRepository {
    return DatabaseFactory.getAdapters().profileRepository;
  }

  /**
   * Repositorio de gastos
   */
  get gastoRepository(): IGastoRepository {
    return DatabaseFactory.getAdapters().gastoRepository;
  }

  /**
   * Repositorio de ingresos
   */
  get ingresoRepository(): IIngresoRepository {
    return DatabaseFactory.getAdapters().ingresoRepository;
  }

  /**
   * Repositorio de stock
   */
  get stockRepository(): IStockRepository {
    return DatabaseFactory.getAdapters().stockRepository;
  }

  /**
   * Repositorio de historial de stock
   */
  get stockHistoricoRepository(): IStockHistoricoRepository {
    return DatabaseFactory.getAdapters().stockHistoricoRepository;
  }

  /**
   * Repositorio de cache de categorías
   */
  get categoriaCacheRepository(): ICategoriaCacheRepository {
    return DatabaseFactory.getAdapters().categoriaCacheRepository;
  }

  /**
   * Repositorio de conceptos (taxonomía)
   */
  get conceptoRepository(): IConceptoRepository {
    return DatabaseFactory.getAdapters().conceptoRepository;
  }

  /**
   * Repositorio de categorías (taxonomía)
   */
  get categoriaRepository(): ICategoriaRepository {
    return DatabaseFactory.getAdapters().categoriaRepository;
  }

  /**
   * Repositorio de notas
   */
  get notaRepository(): INotaRepository {
    return DatabaseFactory.getAdapters().notaRepository;
  }

  /**
   * Repositorio de eventos
   */
  get eventoRepository(): IEventoRepository {
    return DatabaseFactory.getAdapters().eventoRepository;
  }

  /**
   * Repositorio de recordatorios
   */
  get recordatorioRepository(): IRecordatorioRepository {
    return DatabaseFactory.getAdapters().recordatorioRepository;
  }

  /**
   * Repositorio de lluvias (KAN-87)
   */
  get lluviaRepository(): ILluviaRepository {
    return DatabaseFactory.getAdapters().lluviaRepository;
  }

  /**
   * Repositorio de declaraciones SIGSA
   */
  get sigsaDeclaracionRepository(): ISigsaDeclaracionRepository {
    return DatabaseFactory.getAdapters().sigsaDeclaracionRepository;
  }

  /**
   * Repositorio de acciones de sync (cola con ejecución server-side)
   */
  get syncAccionesRepository(): ISyncAccionesRepository {
    return DatabaseFactory.getAdapters().syncAccionesRepository;
  }

  /**
   * Repositorio de elementos de mapa (Mapa del Campo)
   */
  get elementoMapaRepository(): IElementoMapaRepository {
    return DatabaseFactory.getAdapters().elementoMapaRepository;
  }

  /**
   * Repositorio de puntos de control (Mapa del Campo)
   */
  get puntoControlRepository(): IPuntoControlRepository {
    return DatabaseFactory.getAdapters().puntoControlRepository;
  }

  /**
   * Repositorio de eventos (observaciones) de puntos de control
   */
  get puntoControlEventoRepository(): IPuntoControlEventoRepository {
    return DatabaseFactory.getAdapters().puntoControlEventoRepository;
  }

  /**
   * Repositorio de siembras de cada lote de siembra (siembras_lote). null en mobile:
   * no sincroniza esa tabla en esta fase.
   */
  get siembraLoteRepository(): ISiembraLoteRepository | null {
    return DatabaseFactory.getAdapters().siembraLoteRepository ?? null;
  }

  /** Anclas de notas (sesión, animal, lote, ubicación, …) */
  get notaAnclaRepository(): INotaAnclaRepository {
    return DatabaseFactory.getAdapters().notaAnclaRepository;
  }

  /** Personal del campo (quien hace los trabajos) */
  get personaCampoRepository(): IPersonaCampoRepository {
    return DatabaseFactory.getAdapters().personaCampoRepository;
  }

  get apodoRepository(): IApodoRepository | null {
    return DatabaseFactory.getAdapters().apodoRepository ?? null;
  }

  /**
   * Base de datos raw
   */
  get database(): IDatabase {
    return DatabaseFactory.getAdapters().database;
  }

  /**
   * Limpia todos los datos
   */
  async clearAllData(): Promise<void> {
    await DatabaseFactory.getAdapters().database.clearAllData();
  }
}

// ==================== Exports de Conveniencia ====================

/**
 * Obtiene el factory
 */
export function getDatabaseFactory(): DatabaseFactory {
  return DatabaseFactory.getInstance();
}

/**
 * Obtiene información del usuario actual
 */
export async function getCurrentUserInfo(): Promise<{ userId: string; empresaId: string }> {
  return DatabaseFactory.getInstance().profileRepository.getCurrentUserInfo();
}

/**
 * Obtiene todos los repositorios
 */
export function getRepositories() {
  const factory = DatabaseFactory.getInstance();
  return {
    // Repositorios principales
    animal: factory.animalRepository,
    animalHistorico: factory.animalHistoricoRepository,
    ubicacion: factory.ubicacionRepository,
    unidad: factory.unidadRepository,
    empresa: factory.empresaRepository,
    operacion: factory.operacionRepository,
    profile: factory.profileRepository,
    // Repositorios de administración
    gasto: factory.gastoRepository,
    ingreso: factory.ingresoRepository,
    stock: factory.stockRepository,
    stockHistorico: factory.stockHistoricoRepository,
    categoriaCache: factory.categoriaCacheRepository,
    // Taxonomía de conceptos
    concepto: factory.conceptoRepository,
    categoria: factory.categoriaRepository,
    // Repositorios de notas y herramientas
    nota: factory.notaRepository,
    evento: factory.eventoRepository,
    recordatorio: factory.recordatorioRepository,
    lluvia: factory.lluviaRepository,
    sigsaDeclaracion: factory.sigsaDeclaracionRepository,
    syncAcciones: factory.syncAccionesRepository,
    elementoMapa: factory.elementoMapaRepository,
    puntoControl: factory.puntoControlRepository,
    puntoControlEvento: factory.puntoControlEventoRepository,
    siembraLote: factory.siembraLoteRepository,
    notaAncla: factory.notaAnclaRepository,
    personaCampo: factory.personaCampoRepository,
    apodo: factory.apodoRepository,
    // Alias para compatibilidad
    operacionCompraVenta: factory.operacionRepository
  };
}

/**
 * Verifica si el factory está listo
 */
export function isFactoryReady(): boolean {
  return DatabaseFactory.isRegistered();
}
